import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../../api/axiosClient';
import { colors, typo, paperSx, tableHeadCellSx, tableCellSx } from '../../../theme/tokens';
import { PageHeader, SectionHeader, EmptyState } from '../../../components/ui';

/**
 * R7 route-move of the former tenant-scoped SecretDefaultsAudit page.
 *
 * Difference vs tenant version: hits `/api/admin/secret-defaults-audit/*`.
 * The underlying router is identical (same Express Router, re-mounted),
 * so all fields and shape are the same — only the base URL changed.
 *
 * NOTE: This page is the read-only AUDIT surface. The mutating
 * SECRET DEFAULTS editor (PUT /api/secret-defaults/:env) is a different
 * surface and is NOT in scope for this round.
 *
 * Audit export functionality is not yet implemented — deferred to a later
 * round when we have a concrete customer need.
 */

const ENVIRONMENTS = ['Dev01', 'Qa', 'Uat', 'Sbx', 'Perf', 'Prod', 'Integ', 'Newdev'];

interface SecretRequirement {
  key: string;
  description: string;
  required: boolean;
  service: string;
}

interface AuditResponse {
  environment: string;
  expected: SecretRequirement[];
  present: string[];
  missing: SecretRequirement[];
  unknown: string[];
  tenantsAffected: Array<{ tenantCode: string; tenantName: string | null; missingKeys: string[] }>;
  defaultsRowFound: boolean;
}

const SecretDefaultsAuditAdminPage: React.FC = () => {
  const [environment, setEnvironment] = useState<string>('Dev01');

  const { data, isLoading, isFetching, refetch, error } = useQuery<AuditResponse>({
    queryKey: ['admin-secret-defaults-audit', environment],
    queryFn: async () => {
      const resp = await axiosClient.get<AuditResponse>(`/api/admin/secret-defaults-audit/${environment}`);
      return resp.data;
    },
    staleTime: 30_000,
  });

  const presentSet = useMemo(() => new Set(data?.present ?? []), [data?.present]);

  const summary = useMemo(() => {
    if (!data) return { total: 0, present: 0, missingRequired: 0, missingOptional: 0 };
    const missingRequired = data.missing.filter((m) => m.required).length;
    const missingOptional = data.missing.filter((m) => !m.required).length;
    return {
      total: data.expected.length,
      present: data.present.length,
      missingRequired,
      missingOptional,
    };
  }, [data]);

  const hasBlocker = summary.missingRequired > 0;

  return (
    <Box>
      <PageHeader
        title="Secret Defaults Audit"
        subtitle="Compare environment-level secret defaults against the platform services manifest. Missing required keys will cause tenant bootstrap failures."
      />

      <Paper sx={{ ...paperSx, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Environment</InputLabel>
            <Select
              label="Environment"
              value={environment}
              onChange={(e) => setEnvironment(String(e.target.value))}
            >
              {ENVIRONMENTS.map((env) => (
                <MenuItem key={env} value={env}>{env}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={isFetching ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>

          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              size="small"
              label={`Present: ${summary.present} / ${summary.total}`}
              sx={{ backgroundColor: colors.successBg, color: colors.successText }}
            />
            <Chip
              size="small"
              label={`Missing required: ${summary.missingRequired}`}
              sx={{
                backgroundColor: summary.missingRequired > 0 ? colors.errorBg : colors.bgMuted,
                color: summary.missingRequired > 0 ? colors.errorText : colors.textSecondary,
              }}
            />
            <Chip
              size="small"
              label={`Missing optional: ${summary.missingOptional}`}
              sx={{
                backgroundColor: summary.missingOptional > 0 ? colors.warningBg : colors.bgMuted,
                color: summary.missingOptional > 0 ? colors.warningText : colors.textSecondary,
              }}
            />
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load audit: {(error as Error).message}
        </Alert>
      )}

      {data && !data.defaultsRowFound && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningAmberIcon />}>
          No <code>tenant_secret_defaults</code> row found for environment <strong>{data.environment}</strong>.
          Every key is effectively missing. Configure defaults via <code>PUT /api/secret-defaults/{data.environment}</code>.
        </Alert>
      )}

      {data && hasBlocker && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {summary.missingRequired} required key{summary.missingRequired === 1 ? '' : 's'} missing for <strong>{data.environment}</strong>.
          New tenants published to this environment will bootstrap with empty credentials for these services.
        </Alert>
      )}

      <Paper sx={{ ...paperSx, mb: 3, p: 0, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${colors.borderLight}` }}>
          <SectionHeader title="Manifest vs. defaults" subtitle="Status of every key the platform expects" />
        </Box>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : data ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>Service</TableCell>
                  <TableCell sx={tableHeadCellSx}>Key</TableCell>
                  <TableCell sx={tableHeadCellSx}>Required</TableCell>
                  <TableCell sx={tableHeadCellSx}>Status</TableCell>
                  <TableCell sx={tableHeadCellSx}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.expected.map((req) => {
                  const present = presentSet.has(req.key);
                  const statusColor = present
                    ? { bg: colors.successBg, fg: colors.successText }
                    : req.required
                      ? { bg: colors.errorBg, fg: colors.errorText }
                      : { bg: colors.warningBg, fg: colors.warningText };
                  return (
                    <TableRow key={req.key} hover>
                      <TableCell sx={tableCellSx}>
                        <Chip size="small" label={req.service} sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ ...tableCellSx, fontFamily: 'monospace', fontSize: 12 }}>
                        {req.key}
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        {req.required
                          ? <Chip size="small" label="required" color="error" variant="outlined" />
                          : <Chip size="small" label="optional" variant="outlined" />}
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <Chip
                          size="small"
                          icon={present ? <CheckCircleOutlineIcon fontSize="small" /> : <CancelOutlinedIcon fontSize="small" />}
                          label={present ? 'present' : 'missing'}
                          sx={{ backgroundColor: statusColor.bg, color: statusColor.fg, '& .MuiChip-icon': { color: statusColor.fg } }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tableCellSx, color: colors.textSecondary, fontSize: 12 }}>
                        {req.description}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Paper>

      {data && data.unknown.length > 0 && (
        <Paper sx={{ ...paperSx, mb: 3 }}>
          <SectionHeader
            title="Unknown columns"
            subtitle="Columns present in the DB but not in the manifest — update platformRequiredSecrets.ts"
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {data.unknown.map((col) => (
              <Chip key={col} size="small" label={col} sx={{ fontFamily: 'monospace' }} />
            ))}
          </Box>
        </Paper>
      )}

      {data && data.tenantsAffected.length > 0 && (
        <Paper sx={{ ...paperSx, p: 0, overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${colors.borderLight}` }}>
            <SectionHeader
              title="Tenants affected"
              subtitle={`${data.tenantsAffected.length} tenant(s) would bootstrap with missing keys (per-tenant overrides considered)`}
            />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>Tenant Code</TableCell>
                  <TableCell sx={tableHeadCellSx}>Tenant Name</TableCell>
                  <TableCell sx={tableHeadCellSx}>Missing Keys</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.tenantsAffected.map((t) => (
                  <TableRow key={t.tenantCode} hover>
                    <TableCell sx={{ ...tableCellSx, fontFamily: 'monospace', fontSize: 12 }}>{t.tenantCode}</TableCell>
                    <TableCell sx={tableCellSx}>{t.tenantName ?? '—'}</TableCell>
                    <TableCell sx={tableCellSx}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {t.missingKeys.map((k) => (
                          <Chip
                            key={k}
                            size="small"
                            label={k}
                            sx={{ fontFamily: 'monospace', fontSize: 11, backgroundColor: colors.warningBg, color: colors.warningText }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {data && data.tenantsAffected.length === 0 && data.missing.length === 0 && (
        <EmptyState
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 48, color: colors.successIcon }} />}
          title="All keys present"
          description={`Every platform-required secret is configured for ${data.environment}.`}
        />
      )}

      <Divider sx={{ my: 4 }} />
      <Typography sx={{ ...typo.tinyText, color: colors.textTertiary }}>
        Manifest source: <code>server/src/config/platformRequiredSecrets.ts</code>. Add new keys here whenever a platform
        service introduces a required secret, then add the matching column via a new migration.
      </Typography>
    </Box>
  );
};

export default SecretDefaultsAuditAdminPage;
